import { createContext, useContext } from "react";
import { SITE_URL } from "./env";

export interface SeoMeta {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  noIndex?: boolean;
  /** Optional JSON-LD graph rendered into the document as application/ld+json. */
  jsonLd?: unknown;
}

// Escape "<" so a "</script>" inside stringified JSON-LD can't break out of the
// <script> element (stored-XSS guard; mirrors the blog engine's jsonLdSafe).
function jsonLdSafe(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

/**
 * Collector used ONLY by scripts/prerender.mjs.
 *
 * During prerender the whole app is rendered with renderToString, which has no
 * document to hoist metadata into — React would emit <title>/<meta> inline in
 * the body. So the prerender wraps the tree in this provider, <Seo> pushes its
 * props here instead of rendering tags, and the script serialises them into the
 * real <head>. On the client the context is null and <Seo> renders normally.
 */
export const SeoSink = createContext<SeoMeta[] | null>(null);

/** Marks prerendered head tags so the client can drop them before hydrating. */
export const SEO_SSG_ATTR = "data-seo-ssg";

interface TagSpec {
  tag: "title" | "meta" | "link";
  /** Text content — title only. */
  text?: string;
  attrs?: Record<string, string>;
}

/**
 * The ONE definition of a route's head tags.
 *
 * Both consumers derive from this: <Seo> turns it into JSX for the client, and
 * scripts/prerender.mjs serialises it to HTML. Keeping a single source means the
 * prerendered head and the hydrated head cannot drift apart.
 */
export function seoTags(meta: SeoMeta): TagSpec[] {
  const url = `${SITE_URL}${meta.path}`;
  const image = meta.ogImage ?? `${SITE_URL}/og-image.png`;
  return [
    { tag: "title", text: meta.title },
    { tag: "meta", attrs: { name: "description", content: meta.description } },
    { tag: "link", attrs: { rel: "canonical", href: url } },
    { tag: "meta", attrs: { property: "og:title", content: meta.title } },
    { tag: "meta", attrs: { property: "og:description", content: meta.description } },
    { tag: "meta", attrs: { property: "og:url", content: url } },
    { tag: "meta", attrs: { property: "og:type", content: "website" } },
    { tag: "meta", attrs: { property: "og:image", content: image } },
    { tag: "meta", attrs: { property: "og:image:width", content: "1200" } },
    { tag: "meta", attrs: { property: "og:image:height", content: "630" } },
    { tag: "meta", attrs: { name: "twitter:card", content: "summary_large_image" } },
    { tag: "meta", attrs: { name: "twitter:title", content: meta.title } },
    { tag: "meta", attrs: { name: "twitter:description", content: meta.description } },
    { tag: "meta", attrs: { name: "twitter:image", content: image } },
    { tag: "meta", attrs: { name: "robots", content: meta.noIndex ? "noindex" : "index, follow" } },
  ];
}

function escapeAttr(v: string): string {
  return v
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Serialise a route's head tags to HTML. Prerender-side counterpart of <Seo>. */
export function renderSeoHead(meta: SeoMeta): string {
  const parts = seoTags(meta).map((t) => {
    const attrs = Object.entries(t.attrs ?? {})
      .map(([k, v]) => ` ${k}="${escapeAttr(v)}"`)
      .join("");
    return t.tag === "title"
      ? `<title ${SEO_SSG_ATTR}="true">${escapeAttr(t.text ?? "")}</title>`
      : `<${t.tag}${attrs} ${SEO_SSG_ATTR}="true" />`;
  });
  if (meta.jsonLd) {
    parts.push(
      `<script type="application/ld+json" ${SEO_SSG_ATTR}="true">${jsonLdSafe(meta.jsonLd)}</script>`,
    );
  }
  return parts.join("\n    ");
}

/**
 * Per-route document head.
 *
 * Client: renders <title>/<meta>/<link> directly — React 19 hoists these into
 * <head> natively (and re-hoists on SPA navigation), which is why this no longer
 * needs a helmet library. react-helmet-async peered React <=18 and was the last
 * thing pinning this app to the old SSG stack.
 *
 * Prerender: see SeoSink above — tags are collected, not rendered.
 */
export function Seo(meta: SeoMeta) {
  const sink = useContext(SeoSink);
  const jsonLdNode = meta.jsonLd ? (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonLdSafe(meta.jsonLd) }}
    />
  ) : null;

  // Prerender pass: record and emit nothing. renderToString would otherwise
  // place these tags inline in the body.
  if (sink) {
    sink.push(meta);
    return null;
  }

  return (
    <>
      {seoTags(meta).map((t, i) =>
        t.tag === "title" ? (
          <title key={i}>{t.text}</title>
        ) : t.tag === "link" ? (
          <link key={i} {...t.attrs} />
        ) : (
          <meta key={i} {...t.attrs} />
        ),
      )}
      {jsonLdNode}
    </>
  );
}
